'use client';

import { ReactNode, useState } from 'react';

// Utils
import { combineClasses } from '@shared/utils';

// Hooks
import { useBreakpoint } from '@shared/hooks';
import { useDropdownDismissOnResize } from '@/hooks';

// UI
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@shared/ui';
export interface SidebarItemProps {
  icon: ReactNode;
  label: string;
  className?: string;
}
interface SidebarDropdownProps extends SidebarItemProps {
  triggerAriaLabel: string;
  triggerClassName?: string;
  iconVariant?: 'fill' | 'stroke'; // SVG color variant, fill for solid icons or stroke for outlined
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}
const TRIANGLE_CLASSES = combineClasses('absolute -top-[10px] right-[20%] sm:right-[18%]', 'size-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] border-b-white', '[filter:drop-shadow(0_-2px_1px_rgba(0,0,0,0.12))]');
const SidebarDropdown = ({
  icon,
  label,
  className,
  triggerAriaLabel,
  triggerClassName,
  iconVariant = 'fill',
  onOpenChange,
  children
}: SidebarDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    isTablet
  } = useBreakpoint();
  const sideOffset = isTablet ? 4 : 12;
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };
  const closeDropdown = () => handleOpenChange(false);
  const {
    contentRef
  } = useDropdownDismissOnResize(isOpen, closeDropdown);
  const iconColorClass = iconVariant === 'fill' ? '[&_svg]:fill-white sm:group-hover:[&_svg]:fill-tertiary' : '[&_svg]:stroke-white sm:group-hover:[&_svg]:stroke-tertiary';
  return <div className={className} data-sentry-component="SidebarDropdown" data-sentry-source-file="index.tsx">
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange} data-sentry-element="DropdownMenu" data-sentry-source-file="index.tsx">
        <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="index.tsx">
          <button type="button" id={`sidebar-${label}-trigger`} aria-label={triggerAriaLabel} aria-expanded={isOpen} className={combineClasses('relative flex-center sm:flex-col gap-2', 'px-3 py-1 sm:h-25', 'sm:border-y-[5px] border-transparent', 'group outline-none cursor-pointer bg-transparent')}>
            <div className={combineClasses('size-4 sm:size-9 flex-center', 'sm:border-2 sm:border-tertiary sm:rounded-full', iconColorClass)}>
              {icon}
            </div>

            <span className={combineClasses('text-xs sm:text-4xs font-medium', 'uppercase sm:lowercase sm:truncate', 'text-white group-hover:text-tertiary')}>
              {label}
            </span>
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent ref={contentRef} side="bottom" align="end" sideOffset={sideOffset} collisionPadding={8}
      // Prevent Radix from auto-focusing trigger on close, which causes scroll stuttering
      onCloseAutoFocus={e => e.preventDefault()} className="relative min-w-36 overflow-visible z-[9999]" data-sentry-element="DropdownMenuContent" data-sentry-source-file="index.tsx">
          <span aria-hidden className={combineClasses(TRIANGLE_CLASSES, triggerClassName)} />
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
};
export default SidebarDropdown;